/**
 * The account's servers, **buffered on this device** (plan
 * `docs/plans/LOCAL-HTTP-AND-CLOUD-ADOPTION.md` §2/C2 — card `t_f4f925f9`).
 *
 * The owner's words: *"Your servers: should be buffered.. should be from the
 * cloud.. then local info scanned in the bg for availability… The local settings
 * should be buffered and preserved."*
 *
 * ── What this module is, and what it is not ─────────────────────────────────
 *
 * The list is the **cloud's** (`GET /hubs` → `services/accountHubs.ts`), and this
 * is its local copy: one persisted record per server, written whenever a cloud
 * answer arrives and read at launch **before any network call**, so the launcher
 * paints the account's servers instantly and still knows them with the cloud and
 * the LAN both unreachable. It is a cache, not a second source of truth: a
 * server the cloud no longer lists stops being painted (`cloudListed` turns
 * false), and one the cloud adds appears on the first answer.
 *
 * Three kinds of fact live side by side in one record, and the difference decides
 * who may write it:
 *
 *   - **the cloud's** — `name`, `publicId`, `publicKey`, licence facts (plan
 *     §2/C3 added them to `/hubs`: `hubPublicId`, `serverPlan`, `seats`,
 *     `seatsUsed`, `licenseExpiresAt`). A cloud answer *replaces* these outright.
 *   - **this device's** — the LAN addresses that answered, the manual address and
 *     its `manual` flag (plan §2/C4), the `cloudOnly` pin, the last route that
 *     actually opened the hub, and the hub's own **local password** the person
 *     entered to open it (owner, 2026-10-01: *"we need password field, to enter
 *     password (preserved)"* — set in the edit sheet's own settings, never a
 *     cloud fact). A cloud answer must never touch them; they are the part of
 *     "the local settings" that has to survive.
 *   - **derived** — `cloudListed`, `lastCloudSeenAt`, `updatedAt`.
 *
 * ── Storage ────────────────────────────────────────────────────────────────
 *
 * Key `otto_server_profiles` (beside the hub-session keys the app already owns —
 * `otto_active_hub_ip/port/secure`, `otto_active_hub_key`, `otto_saved_connections`;
 * `savedConnections.ts` reads its own key, `hubAccessToken:<host>:<port>` and
 * `torik_device_token:*` are untouched). The payload is versioned:
 *
 *     { version: 1, accountId, savedAt, rows: [ServerProfile, …] }
 *
 * `accountId` is the cloud account the buffer was last filled from — it is what
 * lets a signed-out device (or a second account) be told apart from "offline",
 * and it is the only thing sign-out clears.
 *
 * ── The tolerant reader ────────────────────────────────────────────────────
 *
 * Whatever is in storage was written by some build of this app: a corrupt value,
 * a payload from an older schema, a payload from a **newer** one (a downgrade), a
 * row missing every field this build reads. None of it may crash the launcher,
 * and none of it may take the whole buffer down with it — every field is read
 * through its own guard, an unreadable row is dropped and the readable ones are
 * kept. The version is recorded and re-read, but a version this build does not
 * know is still read field-wise (the fields it does know), because the alternative
 * is silently discarding a user's device settings.
 *
 * What a device that has never written this key does on first launch: nothing.
 * The add is purely additive — every pre-existing key is still read exactly as
 * before, the launcher paints what it painted before, and the buffer fills on the
 * first successful `GET /hubs`. There is no migration step and no key is
 * rewritten or removed.
 */
import { ref } from 'vue';
import { parseHubAddressInput } from '../utils/hubAddress';
import { activeProfileId, onProfileScopeChange, profileScopedKey } from './profileScope';

/** The storage key this module owns. */
export const SERVER_PROFILES_STORAGE_KEY = 'otto_server_profiles';

/** The schema this build writes. */
export const SERVER_PROFILES_SCHEMA_VERSION = 1;

/** Hard cap on buffered records — a device cannot accumulate servers forever. */
export const SERVER_PROFILES_LIMIT = 50;

/** How many addresses are kept per server, most recently seen first. */
export const SERVER_PROFILE_LAN_LIMIT = 4;

/** The slice of `Storage` this module needs (injected by tests). */
export interface ServerProfileStorage {
  getItem(key: string): string | null;
  setItem(key: string, value: string): void;
  removeItem(key: string): void;
}

/** One address that answered for this hub, and when. */
export interface ServerProfileLanAddress {
  /** The host dialled — an IP literal or a name. */
  host: string;
  /**
   * The port dialled. **80 is the hub's front door and is stored as the number,
   * never as "no port"** — the port-less spelling is a URL convention (card
   * `t_c2446418`), and a record that keeps what was measured can always be
   * re-spelled. A record written port-less by a newer build is read as the front
   * door (`HUB_FRONT_DOOR_PORT`).
   */
  port: number;
  /** The scheme measured for that address: `true` = TLS, `undefined` = nothing said. */
  secure?: boolean;
  /** When it last answered (ISO). */
  lastSeenAt: string;
}

/** The licence facts the cloud reported for this hub (plan §2/C3). */
export interface ServerProfileLicence {
  /** The entitlement's plan name, or null. */
  plan: string | null;
  /** The plan's user cap; null = unlimited (or no plan). */
  seats: number | null;
  /** How many people the hub is shared with, as the cloud counts them. */
  seatsUsed: number | null;
  /** The plan grant's expiry (ISO), or null. */
  expiresAt: string | null;
  /**
   * The plan's own **access** fact (`GET /hubs` → `access`; otto-web
   * `lib/hubList.ts`, `FLOOR-PLAN-AND-PLAN-EDITOR.md` §3.2): `local` = the hub
   * is answerable on its own network only, `remote` = the cloud tunnel too.
   * `null` = the cloud did not say (the field is additive on the wire, so an
   * older build sends no key at all) — never read as "local".
   *
   * It travels with the licence because the Local page's way-in table decides
   * on it (`config/onboarding.ts` → `resolveDiscoveredHubPlan`): a hub on
   * local-only terms has no cloud connect to offer.
   */
  access: 'local' | 'remote' | null;
  /** When the cloud answered these facts (ISO). */
  readAt: string;
}

/** The last route that actually opened this hub. */
export interface ServerProfileRoute {
  /** Which door opened the session: the LAN address, or the cloud tunnel. */
  route: 'local' | 'cloud';
  /** The host the session was opened at, when it was a LAN address. */
  host: string | null;
  /** The port of that address. */
  port: number | null;
  /** The scheme of that address. */
  secure?: boolean;
  /** When it opened (ISO). */
  at: string;
}

/**
 * The hub's own **local password**, as this device keeps it for one server: the
 * credential `POST /api/auth/login/local` takes when the WAN is cut.
 *
 * The identifier travels with it — the hub accepts the handle or the email, and
 * the door prefills what the person entered last. It is kept with the buffer and
 * not in a keychain: where a local password is *kept* was the owner's decision
 * (plan §2/C6), and this is it (2026-10-01) — the device's own storage, beside
 * its other per-server facts, nothing to the cloud.
 */
export interface ServerLocalPassword {
  identifier: string;
  password: string;
}

/** One server, as this device remembers it. */
export interface ServerProfile {
  /** The cloud row's id (`GET /hubs` → `hubId`) — lowercased. */
  cloudId: string | null;
  /** The hub's own id (`GET /hubs` → `macAddress`, mDNS) — lowercased. */
  hubId: string | null;
  /** The hub's name, as the cloud last spelled it. */
  name: string | null;
  /** The account's own `publicId` for the hub (`GET /hubs` → `hubPublicId`). */
  publicId: string | null;
  /**
   * The hub's Ed25519 public key, base64url — the key the local claim proves
   * against. Case matters (base64url), so it is trimmed and never lowercased.
   */
  publicKey: string | null;
  /** Addresses that answered on the LAN, most recent first (this device's fact). */
  lanAddresses: ServerProfileLanAddress[];
  /** The address the owner typed for this server on this device (plan §2/C4). */
  manualAddress: string | null;
  /** Whether the manual address is to be used — the `manual` flag (plan §2/C4). */
  manual: boolean;
  /** Pin this server to the tunnel: never dial its LAN address (plan D2, card B4). */
  cloudOnly: boolean;
  /**
   * The hub's own local password this device keeps for this server, or null.
   * Written only by `setServerLocalPassword` (the edit sheet's field), read by
   * the open flows through `serverLocalPasswordFor`, and taken away the moment
   * the hub rejects it — a rejected password must not be replayed.
   */
  localPassword: ServerLocalPassword | null;
  /** Licence facts, as the cloud last reported them. */
  licence: ServerProfileLicence | null;
  /** The last route that opened this hub. */
  lastRoute: ServerProfileRoute | null;
  /** The last time a cloud answer listed this hub (ISO), or null. */
  lastCloudSeenAt: string | null;
  /**
   * False once a later cloud answer did **not** list this hub: the card stops
   * being painted (the buffer is the cloud's list), while the record's
   * device-owned facts are kept for a hub that comes back.
   */
  cloudListed: boolean;
  /** When this record last changed (ISO). */
  updatedAt: string;
}

/** The persisted payload. */
export interface ServerProfileStore {
  version: number;
  /** The cloud account the buffer was last filled from, or null. */
  accountId: string | null;
  /** When the buffer was last written (ISO), or null. */
  savedAt: string | null;
  rows: ServerProfile[];
}

/** What a caller may name a server by: one id, or the pair. */
export type ServerProfileRef = string | { cloudId?: string | null; hubId?: string | null };

/**
 * The buffer, as this session reads it. Reactive: the launcher's cards read it
 * while they render.
 */
export const serverProfiles = ref<ServerProfile[]>([]);

/** The account the buffer belongs to, as it was last written. */
export const serverProfileAccountId = ref<string | null>(null);

// ─────────────────────────────────────────────────────────────────────────────
// Field guards — every field of every row, read through its own
// ─────────────────────────────────────────────────────────────────────────────

function idText(value: unknown): string | null {
  if (typeof value !== 'string') return null;
  const trimmed = value.trim().toLowerCase();
  return trimmed.length ? trimmed : null;
}

/** A key, trimmed and **not** lowercased (base64url is case-sensitive). */
function keyText(value: unknown): string | null {
  if (typeof value !== 'string') return null;
  const trimmed = value.trim();
  return trimmed.length ? trimmed : null;
}

function text(value: unknown): string | null {
  if (typeof value !== 'string') return null;
  const trimmed = value.trim();
  return trimmed.length ? trimmed : null;
}

function bool(value: unknown): boolean {
  return value === true;
}

function count(value: unknown): number | null {
  const parsed = typeof value === 'string' ? Number(value) : value;
  if (typeof parsed !== 'number' || !Number.isFinite(parsed)) return null;
  return parsed;
}

/** An ISO string a reader can trust, or null. */
function isoText(value: unknown): string | null {
  const raw = text(value);
  if (!raw) return null;
  return Number.isNaN(new Date(raw).getTime()) ? null : raw;
}

/** The port of a stored address: a number, or the front door for a port-less record. */
function portNumber(value: unknown, fallback: number): number {
  const parsed = count(value);
  if (parsed === null || parsed <= 0 || parsed > 65535) return fallback;
  return Math.trunc(parsed);
}

function lanAddress(value: unknown): ServerProfileLanAddress | null {
  if (!value || typeof value !== 'object') return null;
  const raw = value as Record<string, unknown>;
  const host = text(raw.host) ?? text(raw.ip);
  if (!host) return null;
  const address: ServerProfileLanAddress = {
    host,
    // No port means the hub's front door (card `t_c2446418`): a URL that spells
    // no port is port 80, not "unset".
    port: portNumber(raw.port, 80),
    lastSeenAt: isoText(raw.lastSeenAt) ?? new Date(0).toISOString(),
  };
  if (typeof raw.secure === 'boolean') address.secure = raw.secure;
  return address;
}

function licenceFacts(value: unknown): ServerProfileLicence | null {
  if (!value || typeof value !== 'object') return null;
  const raw = value as Record<string, unknown>;
  const plan = text(raw.plan) ?? text(raw.serverPlan);
  const seats = count(raw.seats);
  const seatsUsed = count(raw.seatsUsed);
  const expiresAt = isoText(raw.expiresAt) ?? isoText(raw.licenseExpiresAt);
  // The plan's own access fact, verbatim: the two words this app's tables read
  // (`config/onboarding.ts` → `DiscoveredHubLicence`). Anything else — absent,
  // a spelling this build does not know — is `null`, which is "the cloud did
  // not say" and never a silent "local".
  const access = raw.access === 'local' || raw.access === 'remote' ? raw.access : null;
  const readAt = isoText(raw.readAt) ?? new Date(0).toISOString();
  // The access fact alone is worth keeping: a hub whose plan fact arrived
  // without plan/seats is still a hub whose terms the page can read.
  if (plan === null && seats === null && seatsUsed === null && expiresAt === null && access === null) return null;
  return { plan, seats, seatsUsed, expiresAt, access, readAt };
}

function routeFacts(value: unknown): ServerProfileRoute | null {
  if (!value || typeof value !== 'object') return null;
  const raw = value as Record<string, unknown>;
  const route = raw.route === 'local' || raw.route === 'cloud' ? raw.route : null;
  if (!route) return null;
  const out: ServerProfileRoute = {
    route,
    host: text(raw.host) ?? text(raw.ip),
    port: count(raw.port) === null ? null : portNumber(raw.port, 80),
    at: isoText(raw.at) ?? new Date(0).toISOString(),
  };
  if (typeof raw.secure === 'boolean') out.secure = raw.secure;
  return out;
}

function lanAddresses(value: unknown): ServerProfileLanAddress[] {
  if (!Array.isArray(value)) return [];
  const out: ServerProfileLanAddress[] = [];
  for (const entry of value) {
    const address = lanAddress(entry);
    if (!address) continue;
    if (out.some((existing) => sameAddress(existing, address))) continue;
    out.push(address);
    if (out.length >= SERVER_PROFILE_LAN_LIMIT) break;
  }
  return out;
}

function sameAddress(a: { host: string; port: number }, b: { host: string; port: number }): boolean {
  return a.host.toLowerCase() === b.host.toLowerCase() && a.port === b.port;
}

/**
 * The kept local password, as a stored row carries it — readable only when it has
 * a password; the identifier may be empty (the door then prefills what it knows).
 */
function storedLocalPassword(value: unknown): ServerLocalPassword | null {
  if (!value || typeof value !== 'object') return null;
  const raw = value as Record<string, unknown>;
  const password = text(raw.password);
  if (!password) return null;
  return { identifier: text(raw.identifier) ?? '', password };
}

/** One stored row, read field by field — an unreadable row answers null. */
export function readServerProfile(value: unknown): ServerProfile | null {
  if (!value || typeof value !== 'object' || Array.isArray(value)) return null;
  const raw = value as Record<string, unknown>;
  const cloudId = idText(raw.cloudId) ?? idText(raw.id);
  const hubId = idText(raw.hubId) ?? idText(raw.macAddress);
  // A row that names neither id cannot be matched to anything: it is not a
  // server, and keeping it would only be noise the next reconcile has to carry.
  if (!cloudId && !hubId) return null;
  return {
    cloudId,
    hubId,
    name: text(raw.name),
    publicId: idText(raw.publicId) ?? idText(raw.hubPublicId),
    publicKey: keyText(raw.publicKey) ?? keyText(raw.hubPublicKey),
    lanAddresses: lanAddresses(raw.lanAddresses ?? raw.addresses),
    manualAddress: text(raw.manualAddress),
    manual: bool(raw.manual),
    cloudOnly: bool(raw.cloudOnly),
    localPassword: storedLocalPassword(raw.localPassword),
    licence: licenceFacts(raw.licence),
    lastRoute: routeFacts(raw.lastRoute),
    lastCloudSeenAt: isoText(raw.lastCloudSeenAt) ?? isoText(raw.lastSeen),
    cloudListed: raw.cloudListed === undefined ? true : bool(raw.cloudListed),
    updatedAt: isoText(raw.updatedAt) ?? new Date(0).toISOString(),
  };
}

/** The rows of a stored payload — the tolerant half of the reader. */
export function readServerProfileRows(value: unknown): ServerProfile[] {
  if (!Array.isArray(value)) return [];
  const rows: ServerProfile[] = [];
  for (const entry of value) {
    const row = readServerProfile(entry);
    if (!row) continue;
    // Two rows for one server (an older build wrote both spellings apart): the
    // first one wins and the second is folded in, so a lookup cannot answer
    // "which of you?".
    const existing = profileIndex(rows).get(profileKey(row));
    if (existing !== undefined) continue;
    rows.push(row);
  }
  return rows;
}

/** A stored payload, read leniently: anything unusable degrades to an empty store. */
export function readServerProfileStore(value: unknown): ServerProfileStore {
  const payload =
    typeof value === 'string'
      ? (() => {
          try {
            return JSON.parse(value) as unknown;
          } catch {
            return null;
          }
        })()
      : value;
  // A bare array (a payload written by a build that stored the rows alone) is
  // read as the rows it is, rather than as an unusable object.
  if (Array.isArray(payload)) {
    return {
      version: SERVER_PROFILES_SCHEMA_VERSION,
      accountId: null,
      savedAt: null,
      rows: readServerProfileRows(payload),
    };
  }
  if (!payload || typeof payload !== 'object') {
    return { version: SERVER_PROFILES_SCHEMA_VERSION, accountId: null, savedAt: null, rows: [] };
  }
  const raw = payload as Record<string, unknown>;
  const version = count(raw.version);
  return {
    // The version as it was written — a newer build's payload keeps its number
    // until this one writes (and then the fields it does not know are gone; the
    // alternative is refusing to persist anything at all).
    version: version === null ? SERVER_PROFILES_SCHEMA_VERSION : Math.trunc(version),
    accountId: idText(raw.accountId) ?? idText(raw.account),
    savedAt: isoText(raw.savedAt),
    rows: readServerProfileRows(raw.rows ?? raw.servers),
  };
}

// ─────────────────────────────────────────────────────────────────────────────
// Reading and writing the device's storage
// ─────────────────────────────────────────────────────────────────────────────

function defaultStorage(): ServerProfileStorage | null {
  try {
    return typeof localStorage === 'undefined' ? null : localStorage;
  } catch {
    return null;
  }
}

/**
 * The key this profile's buffer is held under: the bare key for a device with no
 * account, `<key>:<accountId>` for a signed-in one (`services/profileScope.ts`).
 * The bare key is deliberately the **device** bucket, which is where every buffer
 * written before this module existed already is — no migration, nothing lost.
 */
function storageKey(): string {
  return profileScopedKey(SERVER_PROFILES_STORAGE_KEY);
}

/**
 * One hydrate per session: the launcher reads the buffer while it renders, the
 * module reads storage once. `resetServerProfilesForTests` clears the guard —
 * and a *restart* is exactly that: in-memory gone, storage untouched.
 */
let hydrated = false;

/**
 * Fill the in-memory buffer from this device's storage. Synchronous by design —
 * this is the call that makes the cards paint **before any network call**.
 *
 * Tolerant in every direction: no storage (a test, SSR), no key (a device that
 * has never had a cloud answer), a corrupt value, a payload from another schema
 * — all answer the rows that could be read, which may be none.
 */
export function hydrateServerProfiles(
  options: { storage?: ServerProfileStorage | null; force?: boolean } = {},
): ServerProfile[] {
  if (hydrated && !options.force) return serverProfiles.value;
  const storage = options.storage === undefined ? defaultStorage() : options.storage;
  let store: ServerProfileStore = {
    version: SERVER_PROFILES_SCHEMA_VERSION,
    accountId: null,
    savedAt: null,
    rows: [],
  };
  if (storage) {
    try {
      store = readServerProfileStore(storage.getItem(storageKey()));
    } catch {
      // A storage that throws on read (private mode, a broken profile) leaves the
      // buffer empty — never a crash in the launcher's first paint.
    }
  }
  serverProfiles.value = store.rows;
  serverProfileAccountId.value = store.accountId;
  hydrated = true;
  return serverProfiles.value;
}

/** Write the buffer back to this device's storage. Failures are the storage's, not the caller's. */
function persist(options: { storage?: ServerProfileStorage | null; accountId?: string | null } = {}): void {
  const storage = options.storage === undefined ? defaultStorage() : options.storage;
  if (!storage) return;
  if (options.accountId !== undefined) serverProfileAccountId.value = idText(options.accountId);
  const payload: ServerProfileStore = {
    version: SERVER_PROFILES_SCHEMA_VERSION,
    accountId: serverProfileAccountId.value,
    savedAt: new Date().toISOString(),
    rows: serverProfiles.value,
  };
  try {
    storage.setItem(storageKey(), JSON.stringify(payload));
  } catch (error) {
    // Quota, a locked profile: the in-memory buffer still answers this session.
    console.warn('[ServerProfiles] could not write the server buffer:', error);
  }
}

/**
 * The key a record is held under: the cloud's id when the record has one, its own
 * id otherwise. Two records always name at least one id (`readServerProfile`).
 */
export function profileKey(row: Pick<ServerProfile, 'cloudId' | 'hubId'>): string {
  return row.cloudId ?? row.hubId ?? '';
}

function profileIndex(rows: ServerProfile[]): Map<string, ServerProfile> {
  const index = new Map<string, ServerProfile>();
  for (const row of rows) {
    for (const id of [row.cloudId, row.hubId]) {
      if (id) index.set(id, row);
    }
  }
  return index;
}

/** Back to the device's own storage, and to an empty buffer. Sign-out, or a test. */
export function clearServerProfiles(options: { storage?: ServerProfileStorage | null } = {}): void {
  serverProfiles.value = [];
  serverProfileAccountId.value = null;
  hydrated = true;
  const storage = options.storage === undefined ? defaultStorage() : options.storage;
  try {
    storage?.removeItem(storageKey());
  } catch {
    // ignore — the in-memory buffer is already empty
  }
}

/**
 * Take away the **device bucket's** buffer — the list a device with no account
 * keeps. Called when the cloud account goes away (`services/signedOutLists.ts`).
 *
 * The device bucket is the **bare** key, deliberately: it is where every buffer
 * written before profiles existed already lives, and a device that has just
 * signed out must not paint the previous account's servers from it.
 *
 * An account's bucket is never touched — signing out is not a delete, and signing
 * back in must find the list exactly as it was left.
 */
export function clearDeviceServerProfiles(options: { storage?: ServerProfileStorage | null } = {}): void {
  const storage = options.storage === undefined ? defaultStorage() : options.storage;
  try {
    storage?.removeItem(profileScopedKey(SERVER_PROFILES_STORAGE_KEY, null));
  } catch {
    // ignore — the key is gone or unreadable, which is the state we wanted
  }
  // Only the device bucket is in memory at that point (`hydrated` is per module):
  // if another profile is active, its rows are this session's, not ours to drop.
  if (activeProfileId() === null) {
    serverProfiles.value = [];
    serverProfileAccountId.value = null;
  }
}

/** Tests only: forget the in-memory buffer (storage untouched — a restart). */
export function resetServerProfilesForTests(): void {
  serverProfiles.value = [];
  serverProfileAccountId.value = null;
  hydrated = false;
}

// ─────────────────────────────────────────────────────────────────────────────
// The buffer the launcher paints
// ─────────────────────────────────────────────────────────────────────────────

/**
 * The records to paint: those the cloud listed, not a hub the cloud has dropped.
 * Reads through `hydrateServerProfiles` so a first call always has the device's
 * own storage behind it.
 */
export function bufferedServerProfiles(): ServerProfile[] {
  hydrateServerProfiles();
  return serverProfiles.value.filter((row) => row.cloudListed);
}

/** The account's buffer: the same rows, with the account it belongs to. */
export function bufferedServerProfileStore(): { accountId: string | null; rows: ServerProfile[] } {
  hydrateServerProfiles();
  return { accountId: serverProfileAccountId.value, rows: serverProfiles.value.filter((row) => row.cloudListed) };
}

/** One record, by either spelling of its id (or by a named pair). */
export function profileFor(ref: ServerProfileRef, rows: ServerProfile[] = serverProfiles.value): ServerProfile | null {
  const wanted = refIds(ref);
  if (!wanted.length) return null;
  for (const row of rows) {
    for (const id of wanted) {
      if (row.cloudId === id || row.hubId === id) return row;
    }
  }
  return null;
}

function refIds(ref: ServerProfileRef): string[] {
  if (typeof ref === 'string') {
    const id = idText(ref);
    return id ? [id] : [];
  }
  const ids = [idText(ref.cloudId), idText(ref.hubId)].filter((id): id is string => !!id);
  return [...new Set(ids)];
}

// ─────────────────────────────────────────────────────────────────────────────
// The cloud's answer — the one writer of the cloud-owned half
// ─────────────────────────────────────────────────────────────────────────────

/** A `GET /hubs` row, as this module reads it (ids + cloud-owned fields). */
export function readCloudProfileRow(value: unknown, readAt: string): ServerProfile | null {
  if (!value || typeof value !== 'object') return null;
  const raw = value as Record<string, unknown>;
  // The same field choice `services/accountHubs.ts` makes (measured 2026-09-24):
  // `hubId` is the cloud **row's** id, `macAddress` the hub's **own** id.
  const cloudId = idText(raw.hubId) ?? idText(raw.id);
  const hubId = idText(raw.macAddress) ?? idText(raw.macAddressId);
  if (!cloudId && !hubId) return null;
  return {
    cloudId,
    hubId,
    name: text(raw.name),
    publicId: idText(raw.hubPublicId),
    publicKey: keyText(raw.publicKey) ?? keyText(raw.hubPublicKey),
    // Device-owned fields are filled by the merge below, never from the cloud.
    lanAddresses: [],
    manualAddress: null,
    manual: false,
    cloudOnly: false,
    localPassword: null,
    licence: licenceFacts({
      serverPlan: raw.serverPlan,
      seats: raw.seats,
      seatsUsed: raw.seatsUsed,
      licenseExpiresAt: raw.licenseExpiresAt,
      access: raw.access,
      readAt,
    }),
    lastRoute: null,
    lastCloudSeenAt: readAt,
    cloudListed: true,
    updatedAt: readAt,
  };
}

/** The rows of a `GET /hubs` answer (either payload shape), as profiles. */
export function readCloudProfileRows(raw: unknown, readAt = new Date().toISOString()): ServerProfile[] {
  const list = Array.isArray(raw)
    ? raw
    : raw && typeof raw === 'object' && Array.isArray((raw as { hubs?: unknown }).hubs)
      ? (raw as { hubs: unknown[] }).hubs
      : [];
  const rows: ServerProfile[] = [];
  for (const entry of list) {
    const row = readCloudProfileRow(entry, readAt);
    if (!row) continue;
    if (profileFor(row, rows)) continue;
    rows.push(row);
  }
  return rows;
}

/**
 * Persist what a cloud answer said: upsert its rows (keeping this device's own
 * facts and filling in the id spelling the record was missing), and mark every
 * record the answer did **not** list as no longer cloud-listed.
 *
 * A record that lost its cloud listing and holds nothing device-owned is dropped:
 * it is a card that would otherwise linger forever with nothing in it.
 */
export function rememberCloudServerProfiles(
  raw: unknown,
  options: { accountId?: string | null; storage?: ServerProfileStorage | null; at?: string } = {},
): ServerProfile[] {
  hydrateServerProfiles(options.storage === undefined ? {} : { storage: options.storage });
  const at = options.at ?? new Date().toISOString();
  const incoming = readCloudProfileRows(raw, at);
  if (!incoming.length) {
    // An answer that lists nothing is not a fact to cache — the same rule
    // `rememberAccountHubs` has always applied. A failure keeps the buffer too:
    // this function is only reached with a payload in hand.
    if (options.accountId !== undefined) {
      persist({ storage: options.storage, accountId: options.accountId });
    }
    return serverProfiles.value;
  }

  const rows = serverProfiles.value.slice();
  const seen = new Set<ServerProfile>();
  for (const row of incoming) {
    const existing = profileFor(row, rows);
    if (!existing) {
      rows.push(row);
      seen.add(row);
      continue;
    }
    mergeCloudRow(existing, row, at);
    seen.add(existing);
  }
  for (const row of rows) {
    if (seen.has(row)) continue;
    row.cloudListed = false;
  }
  serverProfiles.value = prune(rows);
  persist({ storage: options.storage, accountId: options.accountId });
  return serverProfiles.value;
}

/** The cloud's half of a record, replaced; the device's half, kept. */
function mergeCloudRow(profile: ServerProfile, incoming: ServerProfile, at: string): void {
  profile.cloudId = incoming.cloudId ?? profile.cloudId;
  profile.hubId = incoming.hubId ?? profile.hubId;
  profile.name = incoming.name ?? profile.name;
  profile.publicId = incoming.publicId ?? profile.publicId;
  profile.publicKey = incoming.publicKey ?? profile.publicKey;
  if (incoming.licence) profile.licence = incoming.licence;
  profile.cloudListed = true;
  profile.lastCloudSeenAt = at;
  profile.updatedAt = at;
}

/** Drop records with nothing left to remember, then cap the buffer. */
function prune(rows: ServerProfile[]): ServerProfile[] {
  const kept = rows.filter((row) => {
    if (row.cloudListed) return true;
    const deviceFacts =
      row.lanAddresses.length > 0 ||
      !!row.manualAddress ||
      row.manual ||
      row.cloudOnly ||
      !!row.localPassword ||
      !!row.lastRoute;
    return deviceFacts;
  });
  if (kept.length <= SERVER_PROFILES_LIMIT) return kept;
  return kept
    .slice()
    .sort((a, b) => new Date(b.updatedAt).getTime() - new Date(a.updatedAt).getTime())
    .slice(0, SERVER_PROFILES_LIMIT);
}

// ─────────────────────────────────────────────────────────────────────────────
// This device's own facts — the writers the surfaces call
// ─────────────────────────────────────────────────────────────────────────────

interface MutateOptions {
  storage?: ServerProfileStorage | null;
  /** Create a record when the id names a server the buffer does not know yet. */
  create?: boolean;
}

/** Change one record in place, create it on demand, and persist the result. */
function mutateProfile(
  ref: ServerProfileRef,
  change: (row: ServerProfile) => void,
  options: MutateOptions = {},
): ServerProfile | null {
  hydrateServerProfiles(options.storage === undefined ? {} : { storage: options.storage });
  const ids = refIds(ref);
  if (!ids.length) return null;
  const rows = serverProfiles.value.slice();
  let row = profileFor(ref, rows);
  if (!row) {
    if (!options.create) return null;
    const at = new Date().toISOString();
    row = {
      cloudId: typeof ref === 'object' && ref.cloudId ? idText(ref.cloudId) : null,
      hubId: typeof ref === 'object' && ref.hubId ? idText(ref.hubId) : typeof ref === 'string' ? idText(ref) : null,
      name: null,
      publicId: null,
      publicKey: null,
      lanAddresses: [],
      manualAddress: null,
      manual: false,
      cloudOnly: false,
      localPassword: null,
      licence: null,
      lastRoute: null,
      lastCloudSeenAt: null,
      cloudListed: false,
      updatedAt: at,
    };
    rows.push(row);
  }
  change(row);
  row.updatedAt = new Date().toISOString();
  serverProfiles.value = prune(rows);
  persist({ storage: options.storage });
  return row;
}

/**
 * An address answered for this hub — remember it as the last known LAN address.
 * The most recent answer is first, and the same address is not stored twice.
 */
export function rememberKnownLanAddress(
  ref: ServerProfileRef,
  address: { host: string; port?: number; secure?: boolean },
  options: MutateOptions = {},
): ServerProfile | null {
  const host = text(address.host);
  if (!host) return null;
  const port = portNumber(address.port, 80);
  return mutateProfile(
    ref,
    (row) => {
      const entry: ServerProfileLanAddress = { host, port, lastSeenAt: new Date().toISOString() };
      if (typeof address.secure === 'boolean') entry.secure = address.secure;
      row.lanAddresses = [entry, ...row.lanAddresses.filter((known) => !sameAddress(known, entry))].slice(
        0,
        SERVER_PROFILE_LAN_LIMIT,
      );
    },
    // A hub the buffer does not hold is not created here: the buffer is the
    // cloud's list (a LAN-only hub is the Local page's business), so this only
    // sharpens a server that is already remembered.
    { create: false, ...options },
  );
}

/**
 * The route that actually opened this hub, and when. A local route also records
 * the address as a known LAN address — it is the one address measured to answer.
 */
export function rememberLastRoute(
  ref: ServerProfileRef,
  route: { route: 'local' | 'cloud'; host?: string | null; port?: number | null; secure?: boolean },
  options: MutateOptions = {},
): ServerProfile | null {
  const host = text(route.host ?? null);
  const at = new Date().toISOString();
  return mutateProfile(
    ref,
    (row) => {
      const next: ServerProfileRoute = {
        route: route.route,
        host,
        port: host ? portNumber(route.port, 80) : null,
        at,
      };
      if (typeof route.secure === 'boolean') next.secure = route.secure;
      row.lastRoute = next;
      if (route.route === 'local' && host) {
        const address: ServerProfileLanAddress = { host, port: next.port ?? 80, lastSeenAt: at };
        if (typeof route.secure === 'boolean') address.secure = route.secure;
        row.lanAddresses = [address, ...row.lanAddresses.filter((known) => !sameAddress(known, address))].slice(
          0,
          SERVER_PROFILE_LAN_LIMIT,
        );
      }
    },
    // Same rule as the address writer: a route is remembered on a server the
    // buffer already holds, never as a reason to add one.
    { create: false, ...options },
  );
}

/** The manual address of a server on this device, and whether it is to be used. */
export function setServerManualAddress(
  ref: ServerProfileRef,
  address: string | null,
  manual: boolean,
  options: MutateOptions = {},
): ServerProfile | null {
  return mutateProfile(
    ref,
    (row) => {
      row.manualAddress = text(address);
      row.manual = !!row.manualAddress && manual;
    },
    options,
  );
}

/** Pin a server to the tunnel (plan D2's cloud-only override). */
export function setServerCloudOnly(
  ref: ServerProfileRef,
  cloudOnly: boolean,
  options: MutateOptions = {},
): ServerProfile | null {
  return mutateProfile(
    ref,
    (row) => {
      row.cloudOnly = !!cloudOnly;
    },
    options,
  );
}

/**
 * Keep (or forget) this device's copy of the hub's own **local password** for one
 * server — what the edit sheet's own field writes (owner, 2026-10-01: *"we need
 * password field, to enter password (preserved) on this modal (server route
 * settings)"*).
 *
 * `null` forgets the copy; the hub's password itself is not touched by that (it
 * lives on the hub — this is the device's memory of it, so the offline Open
 * spends it without asking). An empty password is stored as `null` rather than
 * as a record with nothing in it.
 */
export function setServerLocalPassword(
  ref: ServerProfileRef,
  entry: ServerLocalPassword | null,
  options: MutateOptions = {},
): ServerProfile | null {
  return mutateProfile(
    ref,
    (row) => {
      const password = text(entry?.password);
      row.localPassword = password ? { identifier: text(entry?.identifier) ?? '', password } : null;
    },
    options,
  );
}

/**
 * The record that keeps a local password for this target — by the account's id or
 * the hub's own when the caller knows either (the address may have changed since
 * the password was entered), by **address** for callers that only have one (the
 * connect dialog's own facts). Addresses are compared the way they are dialled —
 * a manual spelling is read through the same rule the sheet stores it with
 * (`utils/hubAddress.ts`; port-less = the hub's front door).
 */
function profileWithLocalPassword(target: {
  cloudId?: string | null;
  hubId?: string | null;
  host?: string | null;
  port?: number | null;
}): ServerProfile | null {
  const cloudId = idText(target.cloudId);
  const hubId = idText(target.hubId);
  if (cloudId || hubId) {
    for (const row of serverProfiles.value) {
      if (!row.localPassword) continue;
      if ((cloudId && row.cloudId === cloudId) || (hubId && row.hubId === hubId)) return row;
    }
  }

  const host = (target.host ?? '').trim().toLowerCase();
  const port = Number(target.port) || 0;
  if (!host || !port) return null;
  for (const row of serverProfiles.value) {
    if (!row.localPassword) continue;
    const manual = row.manualAddress ? parseHubAddressInput(row.manualAddress) : null;
    const onManual = !!manual && manual.host.trim().toLowerCase() === host && manual.port === port;
    const onLan = row.lanAddresses.some(
      (address) => address.host.trim().toLowerCase() === host && address.port === port,
    );
    if (onManual || onLan) return row;
  }
  return null;
}

/** The local password this device keeps for a hub, or null — what the open flows read before asking. */
export function serverLocalPasswordFor(target: {
  cloudId?: string | null;
  hubId?: string | null;
  host?: string | null;
  port?: number | null;
}): ServerLocalPassword | null {
  const row = profileWithLocalPassword(target);
  return row?.localPassword ? { ...row.localPassword } : null;
}

/**
 * Take the kept copy away — the edit sheet's own **Remove**, and the rule a hub's
 * rejection enforces (a rejected password must not be replayed, the same rule the
 * session store keeps). The hub's own password is never touched by this: it lives
 * on the hub, and this is the device's memory of it. Answers the record it was
 * cleared from, when there was one.
 */
export function forgetServerLocalPassword(
  target: {
    cloudId?: string | null;
    hubId?: string | null;
    host?: string | null;
    port?: number | null;
  },
  options: MutateOptions = {},
): ServerProfile | null {
  const row = profileWithLocalPassword(target);
  if (!row) return null;
  return setServerLocalPassword({ cloudId: row.cloudId, hubId: row.hubId }, null, options);
}

/**
 * The account's own `publicId` for a hub, learned from
 * `GET /hubs/:id/bindings` (`services/hubAccountIdentity.ts`) — sharpened in the
 * buffer too, so the record a launch paints from carries the id the LAN claim is
 * gated on. A record the buffer does not hold is not created: the buffer is the
 * cloud's list, and this only sharpens an entry that is already there.
 */
export function rememberProfilePublicId(
  ref: ServerProfileRef,
  publicId: string | null,
  options: MutateOptions = {},
): ServerProfile | null {
  return mutateProfile(
    ref,
    (row) => {
      row.publicId = idText(publicId);
    },
    options,
  );
}

// ─────────────────────────────────────────────────────────────────────────────
// The profile changed — this buffer is not that profile's any more
// ─────────────────────────────────────────────────────────────────────────────

// The rows in memory belong to the profile that just went away, and painting them
// under the new one is exactly the leak this module's scoping exists to stop. So
// the buffer is dropped and the new profile's bucket is read at once — the
// launcher's own `watch` on `profileScopeVersion` then repaints what its copies
// hold. Registered here (not in a store) so the buffer follows the scope even
// when the launcher is not mounted: the find modal, the router's restore and the
// connect paths all read this module.
onProfileScopeChange(() => {
  hydrated = false;
  serverProfiles.value = [];
  serverProfileAccountId.value = null;
  hydrateServerProfiles({ force: true });
});
